import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token storage — we DUAL-write to sessionStorage (cleared on tab close) AND
// localStorage so refreshes survive while the tab lifecycle controls security.
const TOKEN_KEY = 'dccf_access_token';
// Body-side refresh token. Required for Safari ITP / Firefox Strict where the
// HTTP-only refresh_token COOKIE is silently blocked → cookie-only refresh
// fails → user gets logged out every 24h. Storing the refresh token here lets
// us drive /auth/refresh via the Authorization header + body fallback path.
const REFRESH_KEY = 'dccf_refresh_token';
const getToken = () => sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
const setToken = (token) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_KEY, token);
};
const clearToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
};
const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
const setRefreshToken = (token) => {
  if (token) localStorage.setItem(REFRESH_KEY, token);
};
const clearRefreshToken = () => localStorage.removeItem(REFRESH_KEY);

// --- JWT helpers (client-side exp decode so we can schedule silent refresh) ---
const decodeJwt = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    return payload || null;
  } catch {
    return null;
  }
};

// Attach Authorization header + credentials on every request
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

// --- Single-flight refresh ---
let refreshInFlight = null;
const silentRefresh = async () => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      // Send the refresh_token via BOTH:
      //   - HTTP-only cookie (auto, withCredentials)
      //   - Authorization: Refresh <token> header + request body
      // The header + body paths are the ONLY ones that work on Safari ITP and
      // Firefox Strict where the cross-site cookie is blocked.
      const refreshTok = getRefreshToken();
      const headers = refreshTok ? { Authorization: `Refresh ${refreshTok}` } : undefined;
      const body = refreshTok ? { refresh_token: refreshTok } : {};
      const res = await axios.post(`${API}/auth/refresh`, body, { withCredentials: true, headers });
      const newToken = res.data?.access_token;
      if (newToken) setToken(newToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

// Soft, user-friendly in-progress refresh banner (dedupe by ID).
let refreshToastShown = false;
const showSoftRefreshToast = () => {
  if (refreshToastShown) return;
  refreshToastShown = true;
  toast.loading('Refreshing session — your progress is saved...', { id: 'session-refresh', duration: 6000 });
};
const hideSoftRefreshToast = (success) => {
  refreshToastShown = false;
  if (success) {
    toast.success('Session extended — you’re good to keep working', { id: 'session-refresh', duration: 2500 });
  } else {
    toast.dismiss('session-refresh');
  }
};

// --- Response interceptor: on 401, try refresh ONCE then retry (user never sees "Token expired") ---
axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    if (status === 401 && original && !original._retry) {
      const url = original.url || '';
      if (/\/auth\/(login|register|refresh|logout)/.test(url)) {
        return Promise.reject(error);
      }
      original._retry = true;
      showSoftRefreshToast();
      const ok = await silentRefresh();
      hideSoftRefreshToast(ok);
      if (ok) {
        const token = getToken();
        if (token) original.headers = { ...(original.headers || {}), Authorization: `Bearer ${token}` };
        return axios(original);
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const warningTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);

  const clearTimers = () => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    warningTimerRef.current = null;
    refreshTimerRef.current = null;
  };

  // Schedule: 1) a silent refresh 2 minutes before expiry, 2) a toast warning 1 minute before.
  const scheduleTokenLifecycle = useCallback((token) => {
    clearTimers();
    if (!token) return;
    const payload = decodeJwt(token);
    if (!payload?.exp) return;
    const nowSec = Math.floor(Date.now() / 1000);
    const remaining = payload.exp - nowSec;
    if (remaining <= 0) return;

    // Proactive refresh: 2 minutes before expiry (or halfway if token lifetime < 4 min)
    const refreshAt = Math.max(5, remaining - 120);
    refreshTimerRef.current = setTimeout(async () => {
      const ok = await silentRefresh();
      if (ok) {
        // Reschedule based on new token
        const t = getToken();
        if (t) scheduleTokenLifecycle(t);
      }
    }, refreshAt * 1000);

    // Warning toast: 1 minute before expiry if refresh fails or is unreachable
    if (remaining > 120) {
      warningTimerRef.current = setTimeout(() => {
        toast.warning('Your session will expire soon — save your work.', {
          duration: 10000,
          action: {
            label: 'Extend',
            onClick: async () => {
              const ok = await silentRefresh();
              if (ok) {
                toast.success('Session extended');
                const t = getToken();
                if (t) scheduleTokenLifecycle(t);
              } else {
                toast.error('Could not extend session. Please log in again.');
              }
            },
          },
        });
      }, (remaining - 60) * 1000);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      const t = getToken();
      if (t) scheduleTokenLifecycle(t);
    } catch {
      setUser(null);
      clearToken();
    } finally {
      setLoading(false);
    }
  }, [scheduleTokenLifecycle]);

  useEffect(() => {
    checkAuth();
    return () => clearTimers();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const data = response.data;
    if (data.access_token) {
      setToken(data.access_token);
      scheduleTokenLifecycle(data.access_token);
    }
    // Capture body-side refresh token for Safari/Firefox-strict cookie scenarios.
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const data = response.data;
    if (data.access_token) {
      setToken(data.access_token);
      scheduleTokenLifecycle(data.access_token);
    }
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/portal';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    const data = response.data;
    if (data.access_token) {
      setToken(data.access_token);
      scheduleTokenLifecycle(data.access_token);
    }
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch { /* logout best-effort */ }
    clearTimers();
    clearToken();
    clearRefreshToken();
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch { /* silent */ }
  }, []);

  // Apply a fresh auth session from any auth endpoint (login / register /
  // activation / Google OAuth). Single source of truth so token is written to
  // the canonical TOKEN_KEY, lifecycle timers are scheduled, and user state is
  // populated. Use this from ActivatePage and any future flow that issues a
  // JWT outside the standard `login()` path.
  const applyAuthSession = useCallback((data) => {
    if (!data) return;
    if (data.access_token) {
      setToken(data.access_token);
      scheduleTokenLifecycle(data.access_token);
    }
    if (data.refresh_token) setRefreshToken(data.refresh_token);
    setUser(data);
  }, [scheduleTokenLifecycle]);

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    loginWithGoogle,
    exchangeSession,
    logout,
    checkAuth,
    refreshUser,
    applyAuthSession,
  };

  return (
    <AuthContext.Provider value={value}>
      <PresenceBeacon userId={user?.id} />
      {children}
    </AuthContext.Provider>
  );
};

// Lightweight inner component so we don't import the hook at module top.
// Only pings when there is an authenticated user.
const PresenceBeacon = ({ userId }) => {
  const timerRef = useRef(null);
  useEffect(() => {
    if (!userId) return undefined;
    const ping = () => {
      if (document.hidden) return;
      axios.post(`${API}/presence/heartbeat`, {}, { withCredentials: true }).catch(() => {});
    };
    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(ping, 4 * 60 * 1000);
    };
    const stop = () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
    const onVisibility = () => { if (!document.hidden) { ping(); start(); } };
    const initial = setTimeout(() => { ping(); start(); }, 3000);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(initial);
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userId]);
  return null;
};
